/**
 * Wirtschaftlichkeit (Kapitel K).
 *
 * Der im Gespraech am deutlichsten formulierte Wunsch: „Das allerwichtigste war
 * im ersten Schritt dieses automatisierte Angebot, das die Wirtschaftlichkeit
 * berechnen muss."
 *
 * Gerechnet wird stundenweise ueber ein Jahr: Erzeugung gegen Verbrauch, der
 * Rest in den Speicher, was dann noch bleibt ins Netz. Das ist der einzige
 * ehrliche Weg zum Eigenverbrauchsanteil — eine Faustformel auf Monatsbasis
 * ueberschaetzt ihn regelmaessig.
 *
 * PV-K10 ist hier die tragende Anforderung: Jede Zahl muss sich auf ihre
 * Eingangswerte zurueckfuehren lassen. Deshalb gibt jede Rechnung ihre
 * Zwischenwerte mit heraus, und deshalb steht am Ende eine Aufstellung, die man
 * dem Kunden hinlegen kann. Im Gespraech wurde ein Wettbewerbsprodukt
 * kritisiert, weil es bei der Amortisation sehr optimistisch rechnet —
 * Nachvollziehbarkeit ist hier ein Verkaufsargument.
 */
/**
 * Stundensimulation ueber ein Jahr (PV-K07).
 *
 * Ohne Speicher einfach die stuendliche Ueberdeckung. Mit Speicher wandert der
 * Ueberschuss hinein, solange Platz und Ladeleistung reichen, und kommt bei
 * Bedarf wieder heraus.
 */
export function bilanziere(erzeugungJeStunde, verbrauchJeStunde, speicher) {
    const nutzbar = (speicher?.kapazitaetKwh ?? 0) * (speicher?.nutzbarerAnteil ?? 0.9);
    const wirkungsgrad = speicher?.wirkungsgrad ?? 0.92;
    // Der Wirkungsgrad wird je zur Haelfte auf den Weg hinein und hinaus gelegt.
    const wegHinein = Math.sqrt(wirkungsgrad);
    const wegHinaus = Math.sqrt(wirkungsgrad);
    const maxLaden = speicher?.ladeleistungKw ?? Infinity;
    const maxEntladen = speicher?.entladeleistungKw ?? Infinity;
    let stand = 0;
    let erzeugung = 0;
    let verbrauch = 0;
    let direkt = 0;
    let ausSpeicher = 0;
    let einspeisung = 0;
    let netzbezug = 0;
    let geladen = 0;
    const anzahl = Math.max(erzeugungJeStunde.length, verbrauchJeStunde.length);
    for (let i = 0; i < anzahl; i++) {
        const e = erzeugungJeStunde[i] ?? 0;
        const v = verbrauchJeStunde[i] ?? 0;
        erzeugung += e;
        verbrauch += v;
        const sofort = Math.min(e, v);
        direkt += sofort;
        let ueberschuss = e - sofort;
        let bedarf = v - sofort;
        if (nutzbar > 0) {
            if (ueberschuss > 0 && stand < nutzbar) {
                const hinein = Math.min(ueberschuss, maxLaden, (nutzbar - stand) / wegHinein);
                stand += hinein * wegHinein;
                geladen += hinein * wegHinein;
                ueberschuss -= hinein;
            }
            if (bedarf > 0 && stand > 0) {
                const heraus = Math.min(bedarf, maxEntladen, stand * wegHinaus);
                stand -= heraus / wegHinaus;
                ausSpeicher += heraus;
                bedarf -= heraus;
            }
        }
        einspeisung += ueberschuss;
        netzbezug += bedarf;
    }
    const eigenverbrauch = direkt + ausSpeicher;
    return {
        erzeugungKwh: erzeugung,
        verbrauchKwh: verbrauch,
        direktverbrauchKwh: direkt,
        ausSpeicherKwh: ausSpeicher,
        eigenverbrauchKwh: eigenverbrauch,
        einspeisungKwh: einspeisung,
        netzbezugKwh: netzbezug,
        eigenverbrauchsanteil: erzeugung > 0 ? eigenverbrauch / erzeugung : 0,
        autarkiegrad: verbrauch > 0 ? eigenverbrauch / verbrauch : 0,
        speicherzyklen: nutzbar > 0 ? geladen / nutzbar : 0,
    };
}
export function rechneWirtschaftlichkeit(bilanz, investition, preise) {
    const jahre = preise.jahre ?? 20;
    const degradation = (preise.degradationProzent ?? 0.5) / 100;
    const steigerung = preise.preissteigerungProzent / 100;
    const verguetungsaenderung = (preise.verguetungsaenderungProzent ?? 0) / 100;
    const betriebskosten = preise.betriebskostenJeJahr ?? 0;
    const investitionEuro = investition.gesamtEuro - (investition.foerderungEuro ?? 0);
    const verlauf = [];
    let kumuliert = 0;
    let amortisation = null;
    for (let jahr = 1; jahr <= jahre; jahr++) {
        const alterung = (1 - degradation) ** (jahr - 1);
        const strompreis = preise.strompreisJeKwh * (1 + steigerung) ** (jahr - 1);
        const verguetung = preise.einspeiseverguetungJeKwh * (1 + verguetungsaenderung) ** (jahr - 1);
        const ersparnis = bilanz.eigenverbrauchKwh * alterung * strompreis;
        const erloes = bilanz.einspeisungKwh * alterung * verguetung;
        const ueberschuss = ersparnis + erloes - betriebskosten;
        const vorher = kumuliert;
        kumuliert += ueberschuss;
        // Ohne Investition gibt es nichts zu amortisieren — vorher stand bei
        // fehlenden Preisen „0,0 Jahre" und bei hoher Förderung eine negative Zahl.
        if (amortisation === null && investitionEuro > 0 && kumuliert >= investitionEuro) {
            // Innerhalb des Jahres linear interpolieren, damit nicht immer eine
            // ganze Jahreszahl herauskommt.
            const fehlend = investitionEuro - vorher;
            amortisation = jahr - 1 + (ueberschuss > 0 ? fehlend / ueberschuss : 0);
        }
        verlauf.push({
            jahr,
            erzeugungKwh: bilanz.erzeugungKwh * alterung,
            ersparnisEuro: ersparnis,
            einspeiseerloesEuro: erloes,
            betriebskostenEuro: betriebskosten,
            ueberschussEuro: ueberschuss,
            kumuliertEuro: kumuliert,
        });
    }
    const erste = verlauf[0];
    const euro = (wert) => wert.toLocaleString('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
    return {
        investitionEuro,
        ersparnisErstesJahrEuro: erste?.ersparnisEuro ?? 0,
        einspeiseerloesErstesJahrEuro: erste?.einspeiseerloesEuro ?? 0,
        amortisationJahre: amortisation,
        ueberschussGesamtEuro: kumuliert - investitionEuro,
        renditeProzent: investitionEuro > 0 ? ((kumuliert - investitionEuro) / investitionEuro / jahre) * 100 : 0,
        verlauf,
        bilanz,
        rechenweg: [
            {
                groesse: 'Erzeugung',
                wert: `${Math.round(bilanz.erzeugungKwh)} kWh im Jahr`,
                herkunft: 'Anlagenleistung mal spezifischem Ertrag je Fläche',
            },
            {
                groesse: 'Eigenverbrauch',
                wert: `${Math.round(bilanz.eigenverbrauchKwh)} kWh (${Math.round(bilanz.eigenverbrauchsanteil * 100)} %)`,
                herkunft: 'Stundenweise Gegenüberstellung von Erzeugung und Verbrauch über ein Jahr',
            },
            {
                groesse: 'davon aus dem Speicher',
                wert: `${Math.round(bilanz.ausSpeicherKwh)} kWh`,
                herkunft: `${bilanz.speicherzyklen.toFixed(0)} Vollzyklen im Jahr`,
            },
            {
                groesse: 'Autarkiegrad',
                wert: `${Math.round(bilanz.autarkiegrad * 100)} %`,
                herkunft: 'Eigenverbrauch geteilt durch Jahresverbrauch',
            },
            {
                groesse: 'Ersparnis Jahr 1',
                wert: euro(erste?.ersparnisEuro ?? 0),
                herkunft: `${Math.round(bilanz.eigenverbrauchKwh)} kWh mal ${preise.strompreisJeKwh.toFixed(2)} Euro Strompreis`,
            },
            {
                groesse: 'Einspeiseerlös Jahr 1',
                wert: euro(erste?.einspeiseerloesEuro ?? 0),
                herkunft: `${Math.round(bilanz.einspeisungKwh)} kWh mal ${preise.einspeiseverguetungJeKwh.toFixed(2)} Euro Vergütung`,
            },
            {
                groesse: 'Investition',
                wert: euro(investitionEuro),
                herkunft: investition.foerderungEuro
                    ? `${euro(investition.gesamtEuro)} abzüglich ${euro(investition.foerderungEuro)} Förderung`
                    : 'Summe der Materialliste mit Aufschlag',
            },
            {
                groesse: 'Amortisation',
                wert: amortisation === null ? `nicht innerhalb von ${jahre} Jahren` : `${amortisation.toFixed(1)} Jahre`,
                herkunft: `Kumulierter Überschuss bei ${preise.preissteigerungProzent} % Preissteigerung und ${(degradation * 100).toFixed(1)} % Degradation je Jahr`,
            },
        ],
    };
}
/**
 * Speichergroessen durchrechnen und vergleichen (PV-K08).
 *
 * Ausdruecklicher Wunsch aus dem Gespraech: zeigen, wie sich Eigenverbrauch und
 * Amortisation ueber verschiedene Groessen entwickeln. Der Vorschlag ist die
 * Groesse, ab der jede weitere kWh spuerbar weniger bringt.
 */
export function vergleicheSpeichergroessen(erzeugungJeStunde, verbrauchJeStunde, groessenKwh, preisJeKwhSpeicher, investitionOhneSpeicher, preise) {
    const ohne = bilanziere(erzeugungJeStunde, verbrauchJeStunde);
    const varianten = [];
    for (const kapazitaet of [0, ...groessenKwh]) {
        const bilanz = kapazitaet === 0
            ? ohne
            : bilanziere(erzeugungJeStunde, verbrauchJeStunde, { kapazitaetKwh: kapazitaet });
        const investition = {
            ...investitionOhneSpeicher,
            gesamtEuro: investitionOhneSpeicher.gesamtEuro + kapazitaet * preisJeKwhSpeicher,
        };
        const rechnung = rechneWirtschaftlichkeit(bilanz, investition, preise);
        const mehr = bilanz.eigenverbrauchKwh - ohne.eigenverbrauchKwh;
        varianten.push({
            kapazitaetKwh: kapazitaet,
            eigenverbrauchsanteil: bilanz.eigenverbrauchsanteil,
            autarkiegrad: bilanz.autarkiegrad,
            mehrEigenverbrauchKwh: mehr,
            amortisationJahre: rechnung.amortisationJahre,
            ertragJeKwhSpeicherEuro: kapazitaet > 0 ? (mehr * preise.strompreisJeKwh) / kapazitaet : 0,
        });
    }
    return { varianten, vorschlagKwh: waehleSpeichergroesse(varianten) };
}
/**
 * Die Groesse, ab der die naechste Stufe deutlich weniger bringt.
 *
 * Kein Optimierungsverfahren, sondern die Faustregel, die ein Planer im Kopf
 * anwendet: solange eine Stufe noch mindestens 70 Prozent des Nutzens der
 * vorigen bringt, lohnt sie sich; danach nicht mehr.
 */
function waehleSpeichergroesse(varianten) {
    const mitSpeicher = varianten.filter((v) => v.kapazitaetKwh > 0);
    if (mitSpeicher.length === 0)
        return 0;
    // Bringt schon die kleinste Stufe nichts, wird kein Speicher vorgeschlagen.
    if (!(mitSpeicher[0].mehrEigenverbrauchKwh > 0))
        return 0;
    let beste = mitSpeicher[0];
    for (let i = 1; i < mitSpeicher.length; i++) {
        const vorige = mitSpeicher[i - 1];
        const aktuelle = mitSpeicher[i];
        const zuwachs = aktuelle.mehrEigenverbrauchKwh - vorige.mehrEigenverbrauchKwh;
        const stufe = aktuelle.kapazitaetKwh - vorige.kapazitaetKwh;
        const vorigerZuwachsJeKwh = vorige.mehrEigenverbrauchKwh / vorige.kapazitaetKwh;
        if (stufe <= 0 || vorigerZuwachsJeKwh <= 0)
            break;
        if (zuwachs / stufe < vorigerZuwachsJeKwh * 0.7)
            break;
        beste = aktuelle;
    }
    return beste.kapazitaetKwh;
}
//# sourceMappingURL=wirtschaftlichkeit.js.map